import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconCheckCircle } from '../../assets/images/icons/check_circle_filled.svg';
import { ReactComponent as IconSatellite } from '../../assets/images/icons/icon_satellite.svg';
import { ReactComponent as IconVibration } from '../../assets/images/icons/icon_vibration.svg';
import { useLocale } from '../../contexts/LocaleContext';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import { Species } from '../../types/gql-op-types';
import * as types from '../../types';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import { BOX_CONTENTS, PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import styles from './HardwareFirstLearnMoreModal.module.scss';

// per the figma learn-more overlays: the "why <device>" bullet keys per device, closing on the
// card's tag line so the list ends on who the tracker is for
const WHY_KEYS: Record<PickYourFiDeviceKey, string[]> = {
  mini: [
    'hardware_first_why_6_weeks_battery',
    'hardware_first_why_smart_attachable',
    'hardware_first_why_ai_health',
    'hardware_first_why_silicone_cases',
    'hardware_first_why_mini_fit',
  ],
  s3: [
    'hardware_first_why_3_months_battery',
    'hardware_first_why_all_in_one_collar',
    'hardware_first_why_ai_health',
    'hardware_first_why_led_night_light',
    'hardware_first_why_s3_fit',
  ],
  ultra: [
    'hardware_first_why_5_days_battery',
    'hardware_first_why_smart_attachable',
    'hardware_first_why_gps_satellite',
    'hardware_first_why_callback',
    'hardware_first_why_off_leash',
  ],
};

const DESCRIPTION_KEYS: Record<PickYourFiDeviceKey, string> = {
  mini: 'hardware_first_learn_more_description_mini',
  s3: 'hardware_first_learn_more_description_s3',
  ultra: 'hardware_first_learn_more_description_ultra',
};

interface SpecTableRow {
  labelKey: string;
  valueKey: string;
  subKey?: string;
}

// values are the figma learn-more spec sheets. the one figure not taken from them is ultra's
// battery: that frame's table carries s3's "up to 3 months" row, which its own why-list
// contradicts with "up to 5 days"
const SPEC_TABLE: Record<PickYourFiDeviceKey, SpecTableRow[]> = {
  mini: [
    {
      labelKey: 'hardware_first_spec_label_battery',
      valueKey: 'hardware_first_spec_mini_battery_weeks',
      subKey: 'hardware_first_spec_mini_battery_sub_short',
    },
    { labelKey: 'hardware_first_spec_label_weight', valueKey: 'hardware_first_spec_mini_weight_oz' },
    { labelKey: 'hardware_first_spec_label_attachment', valueKey: 'hardware_first_spec_value_attaches_any_collar' },
    { labelKey: 'hardware_first_spec_label_charging', valueKey: 'hardware_first_spec_value_usb_c' },
    { labelKey: 'hardware_first_spec_label_waterproof', valueKey: 'hardware_first_spec_value_ip68' },
    { labelKey: 'hardware_first_spec_label_night_light', valueKey: 'hardware_first_spec_value_none' },
    { labelKey: 'hardware_first_spec_label_cellular', valueKey: 'hardware_first_spec_verizon' },
    { labelKey: 'hardware_first_spec_label_satellite', valueKey: 'hardware_first_spec_value_none' },
  ],
  s3: [
    { labelKey: 'hardware_first_spec_label_battery', valueKey: 'hardware_first_spec_s3_battery' },
    { labelKey: 'hardware_first_spec_label_weight', valueKey: 'hardware_first_spec_s3_weight_oz' },
    { labelKey: 'hardware_first_spec_label_attachment', valueKey: 'hardware_first_spec_s3_attachment' },
    { labelKey: 'hardware_first_spec_label_charging', valueKey: 'hardware_first_spec_s3_charging' },
    { labelKey: 'hardware_first_spec_label_waterproof', valueKey: 'hardware_first_spec_value_ip68_66k' },
    { labelKey: 'hardware_first_spec_label_night_light', valueKey: 'hardware_first_spec_value_yes' },
    { labelKey: 'hardware_first_spec_label_cellular', valueKey: 'hardware_first_spec_s3_cellular' },
    { labelKey: 'hardware_first_spec_label_satellite', valueKey: 'hardware_first_spec_value_none' },
  ],
  ultra: [
    { labelKey: 'hardware_first_spec_label_battery', valueKey: 'hardware_first_spec_ultra_battery' },
    { labelKey: 'hardware_first_spec_label_weight', valueKey: 'hardware_first_spec_ultra_weight_oz' },
    { labelKey: 'hardware_first_spec_label_attachment', valueKey: 'hardware_first_spec_value_attaches_any_collar' },
    { labelKey: 'hardware_first_spec_label_charging', valueKey: 'hardware_first_spec_value_usb_c' },
    { labelKey: 'hardware_first_spec_label_waterproof', valueKey: 'hardware_first_spec_value_ip68_66k' },
    { labelKey: 'hardware_first_spec_label_night_light', valueKey: 'hardware_first_spec_value_no' },
    { labelKey: 'hardware_first_spec_label_cellular', valueKey: 'hardware_first_spec_ultra_cellular' },
    { labelKey: 'hardware_first_spec_label_satellite', valueKey: 'hardware_first_spec_value_yes' },
  ],
};

interface HardwareFirstLearnMoreModalProps {
  deviceKey: PickYourFiDeviceKey;
  species?: Species;
  name: string;
  discount: types.HardwareDiscountPreview | undefined;
  trigger: JSX.Element;
  onOpen?: () => void;
  /** Called when the bottom CTA is tapped; the modal closes itself first. */
  onSelect: () => void;
}

/**
 * The per-device detail sheet behind each card's "Learn more". The device name sits in a yellow
 * band that scrolls with the content, matching the plan modal's treatment; the cta stays docked.
 */
export default function HardwareFirstLearnMoreModal({
  deviceKey,
  species = Species.DOG,
  name,
  discount,
  trigger,
  onOpen,
  onSelect,
}: HardwareFirstLearnMoreModalProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  // the closing bullet is the card's tag line, which names the dogs a device suits
  const whyKeys =
    species === Species.CAT
      ? WHY_KEYS[deviceKey].map((key) => (key === 'hardware_first_tag_mini' ? 'hardware_first_tag_mini_cats' : key))
      : WHY_KEYS[deviceKey];

  return (
    <SimpleDismissableModal
      trigger={trigger}
      onOpen={onOpen}
      containerClassName={styles.modal}
      closeIconClassName={styles.closeIcon}
    >
      {(close) => (
        <div className={styles.container}>
          <div className={styles.scroll}>
            <div className={styles.hero}>
              <h2 className={styles.heroTitle}>{name}</h2>
            </div>
            <div className={styles.body}>
              <div className={styles.header}>
                <div className={styles.description}>{t(DESCRIPTION_KEYS[deviceKey])}</div>
                {discount && (
                  <div className={styles.priceBlock}>
                    <span>
                      <span className={styles.price}>
                        {formatHardwarePrice(discount.discountedPriceInCents, locale)}
                      </span>
                      {discount.listPriceInCents > discount.discountedPriceInCents && (
                        <span className={styles.listPrice}>
                          {formatHardwarePrice(discount.listPriceInCents, locale)}
                        </span>
                      )}
                    </span>
                    <span className={styles.planLine}>{t('hardware_first_plus_membership')}</span>
                  </div>
                )}
              </div>
              <div className={styles.section}>
                <div className={styles.eyebrow}>{t('hardware_first_why_device', { device: name })}</div>
                <div className={styles.whyList}>
                  {whyKeys.map((key) => (
                    <div key={key} className={styles.whyRow}>
                      <IconCheckCircle className={styles.whyCheck} />
                      <span>{t(key)}</span>
                    </div>
                  ))}
                </div>
              </div>
              {deviceKey === 'ultra' && (
                <>
                  <div className={styles.satelliteCard}>
                    <span className={styles.satelliteIcon}>
                      <IconSatellite />
                    </span>
                    <span className={styles.satelliteTitle}>{t('hardware_first_powered_by_satellite')}</span>
                    <span className={styles.satelliteBody}>{t('hardware_first_powered_by_satellite_body')}</span>
                  </div>
                  <div className={styles.satelliteCard}>
                    <span className={styles.satelliteIcon}>
                      <IconVibration />
                    </span>
                    <span className={styles.satelliteTitle}>{t('ultra_callback_technology')}</span>
                    <span className={styles.satelliteBody}>{t('hardware_first_callback_body')}</span>
                  </div>
                </>
              )}
              <div className={styles.section}>
                <div className={styles.eyebrow}>{t('specs')}</div>
                <div className={styles.specTable}>
                  {SPEC_TABLE[deviceKey].map((row) => (
                    <div key={row.labelKey} className={styles.specTableRow}>
                      <span className={styles.specLabel}>{t(row.labelKey)}</span>
                      <span className={styles.specValue}>
                        {t(row.valueKey)}
                        {row.subKey && <span className={styles.specValueSub}>{t(row.subKey)}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.section}>
                <div className={styles.eyebrow}>{t('cart_item_v2.whats_included')}</div>
                <div className={styles.boxGrid}>
                  {BOX_CONTENTS[deviceKey].map((item) => (
                    <div key={item.url} className={styles.boxItem}>
                      <img className={styles.boxImage} src={item.url} alt={t(item.labelKey)} />
                      <div className={styles.boxLabel}>{t(item.labelKey)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.ctaBar}>
            <button
              type="button"
              className={styles.cta}
              onClick={() => {
                close();
                onSelect();
              }}
            >
              {deviceKey === 's3'
                ? t('hardware_first_select_size_color')
                : t('hardware_first_select_device', { device: name })}
            </button>
          </div>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
